import React from "react"
import { TerraLink } from "../../component/TerraLink/TerraLink"
import {
  darkTextureWrapper,
  colorSettingDetection,
  renderHeaderConditionally,
} from "../../utils/functions"
import {
  formatFocusArea,
  focusAreaToCssPosition,
} from "../../utils/formatFocusArea"
import {
  HERO_SOURCES,
  heroSrc,
  IMAGE_QUALITY_MOBILE,
  IMAGE_QUALITY_TABLET,
} from "@/lib/heroImageUtils"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import {
  layoutSubheaderClasses,
  layoutHeaderClasses,
} from "@/components/tailwindHelpers"
import { Breadcrumbs } from "@/components/terra/component/Breadcrumbs/Breadcrumbs"
import type { BreadcrumbCrumb } from "@/lib/breadcrumbs"
import type { HeroHeaderSize } from "@/lib/heroHeaderSize"

export interface SimpleHeroImageProps {
  title?: string
  height?: number
  width: number
  url: string
  altText?: string
  focusArea?: string
  imageConfiguration?: string | undefined 
  hideImageOnMobile?: boolean
}

export interface SimpleHeroProps {
  eyebrow?: string
  header?: string
  headerElement?: "h1" | "h2"
  subheader?: string
  description?: string
  backgroundImage?: SimpleHeroImageProps
  overlayOption?: "dark" | "light" | "none"
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  buttons?: TerraLink[]
  /** Visual size of the header text. Defaults to "normal" (see MXP-2076). */
  headerSize?: HeroHeaderSize
  isWrapped?: boolean
  searchComponent?: any
  componentImagePriority?: "high" | "auto" | "low"
  /** Marks this hero as the Largest Contentful Paint element on the page.
   *  When true: uses synchronous decoding and forces high fetch priority.
   *  Preload links are generated server-side in renderPage.tsx. */
  isLCP?: boolean
  imageConfiguration?: string
  hideImageOnMobile?: boolean
  breadcrumbs?: BreadcrumbCrumb[]
  breadcrumbDarkMode?: boolean
}

const HEADER_TW = [
  "text-gray-m9 text-[20px] lg:text-[76px] font-light leading-none tracking-wide",
  "max-lg:text-4xl max-lg:leading-none max-lg:tracking-wider lg:!mb-8",
  "t2-dark:text-white",
].join(" ")

const CONTENT = [
  "basis-full max-w-full px-2.5 ml-0",
  "min-lg:basis-[66.666%] min-lg:max-w-[66.666%]",
  "min-lg:basis-1/2 min-lg:max-w-[50%]",
  "relative z-[3] w-full",
].join(" ")

function HeroBackgroundImage({
  image,
  priority,
  isLCP,
  imageConfiguration,
  hideImageOnMobile
}: {
  image: SimpleHeroImageProps
  priority: "high" | "auto" | "low"
  isLCP?: boolean,
  imageConfiguration: string | undefined
  hideImageOnMobile?: boolean
}) {
  const focus = formatFocusArea(image.focusArea || "Center")
  const objectPos = focusAreaToCssPosition(image.focusArea)
  const base = image.url

  const { mobile, tablet, desktop, desktop2x } = HERO_SOURCES
  // Per-band quality. These URLs must stay byte-identical to the matching
  // preload in renderPage, or the browser fetches the hero twice.
  const mobileQ = IMAGE_QUALITY_MOBILE
  const tabletQ = IMAGE_QUALITY_TABLET

  const imageConfigStyles = `${imageConfiguration === "Left Image" ? "relative md:absolute md:w-1/2 h-full" : imageConfiguration === "Right Image" ? "relative md:absolute md:w-1/2 h-full right-0 left-auto" : ""} ${hideImageOnMobile ? "hidden md:block" : ""}`

  return (
    <picture className={`absolute inset-0 z-[1] block ${imageConfigStyles}`}>
      {/* Mobile ≤639px */}
      <source
        type="image/avif"
        media="(max-width: 639px)"
        srcSet={heroSrc(base, mobile.w, mobile.h, focus, "avif", mobileQ)}
      />
      <source
        type="image/webp"
        media="(max-width: 639px)"
        srcSet={heroSrc(base, mobile.w, mobile.h, focus, "webp", mobileQ)}
      />
      {/* Tablet 640–1023px */}
      <source
        type="image/avif"
        media="(max-width: 1023px)"
        srcSet={heroSrc(base, tablet.w, tablet.h, focus, "avif", tabletQ)}
      />
      <source
        type="image/webp"
        media="(max-width: 1023px)"
        srcSet={heroSrc(base, tablet.w, tablet.h, focus, "webp", tabletQ)}
      />
      {/* Desktop ≥1024px with 2× high-DPI */}
      <source
        type="image/avif"
        srcSet={`${heroSrc(base, desktop.w, desktop.h, focus, "avif", 85)} 1x, ${heroSrc(base, desktop2x.w, desktop2x.h, focus, "avif", 70)} 2x`}
      />
      <source
        type="image/webp"
        srcSet={`${heroSrc(base, desktop.w, desktop.h, focus, "webp", 85)} 1x, ${heroSrc(base, desktop2x.w, desktop2x.h, focus, "webp", 70)} 2x`}
      />
      {/* Fallback */}
      <img
        src={heroSrc(base, desktop.w, desktop.h, focus, "webp", 85)}
        alt={image.altText || ""}
        width={desktop.w}
        height={desktop.h}
        className="w-full h-full object-cover"
        style={{ objectPosition: objectPos }}
        loading={isLCP ? "eager" : "lazy"}
        fetchPriority={isLCP ? "high" : priority}
        decoding="async"
      />
    </picture>
  )
}

export const SimpleHero = ({
  eyebrow,
  header,
  headerElement = "h1",
  subheader,
  description,
  backgroundImage,
  overlayOption,
  presetTheme,
  buttons,
  headerSize = "normal",
  isWrapped,
  searchComponent,
  componentImagePriority,
  imageConfiguration,
  isLCP,
  hideImageOnMobile = false,
  breadcrumbs,
  breadcrumbDarkMode = false,
}: SimpleHeroProps) => {
  const detectedColor = colorSettingDetection(presetTheme || "")
  const colorSetting =
    overlayOption === "dark" ? "t2-color-setting--dark" : detectedColor
  const wrapperClasses = `${isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : ""} ${imageConfiguration === "Left Image" ? " flex flex-row-reverse max-md:pt-[120px]" : imageConfiguration === "Right Image" ? "flex flex-row max-md:pt-[120px]" : ""}`
  // "Small" reuses the shared layout header style (matches the site's H2s per MXP-2076).
  const headerClasses =
    headerSize === "small"
      ? layoutHeaderClasses
      : `${HEADER_TW} ${headerSize === "large" ? "t2-big" : ""}`
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  const componentStyles = darkTextureWrapper(presetTheme || "")

  const overlayClass =
    overlayOption === "dark"
      ? "absolute inset-0 z-[2] pointer-events-none bg-[linear-gradient(270deg,#252a2e00,#252a2ee6)]"
      : overlayOption === "light"
        ? "absolute inset-0 z-[2] pointer-events-none bg-[linear-gradient(270deg,#fff0,#ffffffe6)]"
        : null

  const halfWidthImage = imageConfiguration === "Left Image" || imageConfiguration === "Right Image"
  const hasBreadcrumbs = !!breadcrumbs?.length
  const breadcrumbsLightBackground = colorSetting !== "t2-color-setting--dark"

  return (
    <div
      className={`simple-hero ${hasBreadcrumbs ? "pt-10" : "pt-[120px]"} pb-[120px] relative overflow-hidden ${presetTheme} ${colorSetting} ${halfWidthImage ? "max-md:pt-0" : ""}`}
      data-testid="simple-hero"
    >
      {/* Theme texture (theme-seven / action-theme-two) */}
      {componentStyles && (
        <div
          className={`absolute inset-0 z-0 bg-cover bg-center bg-no-repeat ${presetTheme}`}
          style={componentStyles}
        />
      )}

      {/* Background image — always absolute, no JS, no layout shift */}
      {(backgroundImage && imageConfiguration !== "No Image") && (
        <HeroBackgroundImage
          image={backgroundImage}
          priority={componentImagePriority ?? "high"}
          isLCP={isLCP}
          imageConfiguration={imageConfiguration}
          hideImageOnMobile={hideImageOnMobile}
        />
      )}

      {/* Gradient overlay */}
      {overlayClass && <div className={overlayClass} />}
      {/* Content */}
      <div className={wrapperClasses}>
        {/* Half-width image layouts make the wrapper a flex row, so the trail
            stays inside the content column there. */}
        {hasBreadcrumbs && breadcrumbs && !halfWidthImage && (
          <Breadcrumbs
            items={breadcrumbs}
            darkMode={breadcrumbDarkMode}
            lightBackground={breadcrumbsLightBackground}
            className={`relative z-[3] mb-[60px] ${isWrapped ? "" : "w-full px-5 lg:px-20 m-auto max-w-[1440px]"}`}
          />
        )}
        <div className={`flex flex-row flex-wrap mx-[-10px] flex-[0_1_auto] ${halfWidthImage ? "md:w-1/2 " : "" }`}>
          <div className={`${CONTENT} t2-header-area ${halfWidthImage ? "w-full min-lg:basis-full min-lg:max-w-full" : "" }`}>
            {hasBreadcrumbs && breadcrumbs && halfWidthImage && (
              <Breadcrumbs
                items={breadcrumbs}
                darkMode={breadcrumbDarkMode}
                lightBackground={breadcrumbsLightBackground}
                className="mb-[60px]"
              />
            )}
            {eyebrow && (
              <div className="t2-layout__eyebrow t2-hero__eyebrow t2-dark:text-yellow-m10">
                {eyebrow}
              </div>
            )}
            {finalHeader}
            {subheader && (
              <div className={layoutSubheaderClasses}>{subheader}</div>
            )}
            {description && (
              <CommonDescription
                description={description}
                className="t2-simple-hero__description t2-layout__description"
              />
            )}
            {searchComponent && <div>{searchComponent}</div>}
            {buttons && (
              <div
                className={`t2-hero__cta t2-layout__cta mt-10 ${
                  halfWidthImage
                    ? "max-lg:!flex-col max-lg:!gap-y-[30px]"
                    : ""
                }`}
              >
                {buttons.map((btn, index) => {
                  return (
                    <TerraLink key={`cta-${btn?.label}-${index}`} {...btn} />
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
